/**
 * Canicule : before/after comparison slider (honest editorial use)
 * Touch-friendly 44px handle · auto-demo · prefers-reduced-motion → static 50/50
 */
(function () {
  'use strict';

  var LOOP_MIN = 0.25;
  var LOOP_MAX = 0.75;
  var LEG_MS = 4000;
  var SWEEP_MS = 4000;
  var IDLE_RESUME_MS = 3000;

  function easeInOut(t) {
    return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
  }

  function initComparisonSlider(wrapper) {
    var handle = wrapper.querySelector('[data-comparison-handle]');
    var input = wrapper.querySelector('[data-comparison-input]');
    var afterLayer = wrapper.querySelector('.canicule-comparison-slider__layer--after');
    if (!handle || !afterLayer) return;

    var motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    var ratio = 0.5;
    var dragging = false;
    var userPaused = false;
    var hasSweepDone = false;
    var hasStarted = false;
    var rafId = null;
    var idleTimer = null;
    var anim = {
      phase: 'sweep',
      start: 0,
      from: 0,
      to: 1
    };

    function prefersReducedMotion() {
      return motionQuery.matches;
    }

    function setRatio(value, options) {
      options = options || {};
      ratio = Math.max(0, Math.min(1, value));
      var pctNum = Math.round(ratio * 100);
      var pct = pctNum.toFixed(2) + '%';
      handle.style.left = pct;
      afterLayer.style.clipPath = 'inset(0 0 0 ' + pct + ')';
      wrapper.style.setProperty('--slider-pct', String(pctNum));
      handle.setAttribute('aria-valuenow', String(pctNum));
      if (input) input.value = String(pctNum);

      if (options.moving) {
        wrapper.classList.add('is-moving');
      }
    }

    function clearMovingSoon() {
      window.clearTimeout(setRatio._moveTimer);
      setRatio._moveTimer = window.setTimeout(function () {
        wrapper.classList.remove('is-moving');
      }, 120);
    }

    function stopAutoDemo() {
      if (rafId) {
        window.cancelAnimationFrame(rafId);
        rafId = null;
      }
      wrapper.classList.remove('is-auto-demo');
      wrapper.classList.remove('is-moving');
    }

    function startLoopLeg(from, to, timestamp) {
      anim.phase = 'loop';
      anim.start = timestamp;
      anim.from = from;
      anim.to = to;
    }

    function tick(timestamp) {
      if (!anim.start) anim.start = timestamp;
      var elapsed = timestamp - anim.start;
      var duration = anim.phase === 'sweep' ? SWEEP_MS : LEG_MS;
      var progress = Math.min(elapsed / duration, 1);
      var eased = easeInOut(progress);
      var value = anim.from + (anim.to - anim.from) * eased;

      setRatio(value, { moving: true });
      clearMovingSoon();

      if (progress >= 1) {
        if (anim.phase === 'sweep') {
          hasSweepDone = true;
          startLoopLeg(1, LOOP_MIN, timestamp);
        } else if (anim.to === LOOP_MAX) {
          startLoopLeg(LOOP_MAX, LOOP_MIN, timestamp);
        } else {
          startLoopLeg(LOOP_MIN, LOOP_MAX, timestamp);
        }
      }

      if (!userPaused && !dragging && !prefersReducedMotion()) {
        rafId = window.requestAnimationFrame(tick);
      } else {
        rafId = null;
      }
    }

    function startAutoDemo() {
      if (prefersReducedMotion() || dragging || userPaused || rafId) return;

      wrapper.classList.add('is-auto-demo');
      anim.start = 0;

      if (!hasSweepDone) {
        anim.phase = 'sweep';
        anim.from = 0;
        anim.to = 1;
        setRatio(0);
      } else {
        anim.phase = 'loop';
        anim.from = ratio <= LOOP_MIN ? LOOP_MIN : ratio >= LOOP_MAX ? LOOP_MAX : ratio;
        anim.to = anim.from >= LOOP_MAX ? LOOP_MIN : LOOP_MAX;
      }

      rafId = window.requestAnimationFrame(tick);
    }

    function pauseAutoDemo() {
      userPaused = true;
      stopAutoDemo();
      window.clearTimeout(idleTimer);
      idleTimer = window.setTimeout(function () {
        userPaused = false;
        if (!dragging && !prefersReducedMotion()) startAutoDemo();
      }, IDLE_RESUME_MS);
    }

    function positionFromEvent(event) {
      var rect = wrapper.getBoundingClientRect();
      var clientX = event.touches ? event.touches[0].clientX : event.clientX;
      return (clientX - rect.left) / rect.width;
    }

    function onPointerDown(event) {
      dragging = true;
      userPaused = true;
      stopAutoDemo();
      window.clearTimeout(idleTimer);
      wrapper.classList.add('is-dragging');
      setRatio(positionFromEvent(event));
      event.preventDefault();
    }

    function onPointerMove(event) {
      if (!dragging) return;
      setRatio(positionFromEvent(event));
      event.preventDefault();
    }

    function onPointerUp() {
      if (!dragging) return;
      dragging = false;
      wrapper.classList.remove('is-dragging');
      pauseAutoDemo();
    }

    function onUserInput() {
      if (!dragging) pauseAutoDemo();
    }

    if (input) {
      input.addEventListener('input', function () {
        setRatio(Number(input.value) / 100);
        onUserInput();
      });
      input.addEventListener('keydown', onUserInput);
      input.addEventListener('focus', onUserInput);
    }

    handle.addEventListener('mousedown', onPointerDown);
    handle.addEventListener('touchstart', onPointerDown, { passive: false });
    handle.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === 'Home' || e.key === 'End') {
        onUserInput();
      }
    });
    wrapper.addEventListener('mousedown', function (e) {
      if (e.target === handle) return;
      onPointerDown(e);
    });
    window.addEventListener('mousemove', onPointerMove);
    window.addEventListener('touchmove', onPointerMove, { passive: false });
    window.addEventListener('mouseup', onPointerUp);
    window.addEventListener('touchend', onPointerUp);

    function applyMotionPreference() {
      if (prefersReducedMotion()) {
        userPaused = true;
        stopAutoDemo();
        window.clearTimeout(idleTimer);
        setRatio(0.5);
      } else if (!dragging && !userPaused && hasStarted) {
        startAutoDemo();
      }
    }

    motionQuery.addEventListener('change', applyMotionPreference);

    if (prefersReducedMotion()) {
      setRatio(0.5);
      return;
    }

    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting && !hasStarted) {
              hasStarted = true;
              observer.disconnect();
              startAutoDemo();
            }
          });
        },
        { threshold: 0.35, rootMargin: '0px 0px -5% 0px' }
      );
      observer.observe(wrapper);
    } else {
      hasStarted = true;
      startAutoDemo();
    }
  }

  function init() {
    document.querySelectorAll('[data-comparison-slider]').forEach(initComparisonSlider);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
